import React from 'react';
import GavelIcon from '@mui/icons-material/Gavel';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export const FEATURES = [
  {
    icon: <ApartmentOutlinedIcon />,
    title: 'Multi-Tenant',
    desc: 'Consultancies manage consent for all their clients from one dashboard — each org isolated with its own users, notices & data. Switch tenants in one click.',
  },
  {
    icon: <GavelIcon />,
    title: 'Purpose Management',
    desc: 'Define every reason you collect data — owner, category, linked PII, and expiry — with full lifecycle control.',
  },
  {
    icon: <FactCheckOutlinedIcon />,
    title: 'Notice Builder',
    desc: 'Group purposes into branded notices and move them draft → published → archived without code.',
  },
  {
    icon: <HistoryEduOutlinedIcon />,
    title: 'Immutable Audit Trail',
    desc: 'Every consent action append-only — ready for the Data Protection Board under the DPDP Act.',
  },
  {
    icon: <CookieOutlinedIcon />,
    title: 'Cookie Consent',
    desc: 'Drop-in Klaro banner with live accept / reject metrics.',
  },
  {
    icon: <VpnKeyOutlinedIcon />,
    title: 'RBAC & SSO',
    desc: 'Role-based access on every endpoint, with Google OAuth out of the box.',
  },
  {
    icon: <NotificationsActiveOutlinedIcon />,
    title: 'Expiry & Renewal',
    desc: 'Auto-trigger renewal notices so consent never goes stale.',
  },
  {
    icon: <GroupsOutlinedIcon />,
    title: 'Team Management',
    desc: 'Invite by email and manage roles across organizations.',
  },
];

export const STEPS = [
  { num: '01', title: 'Define purposes', desc: 'Set PII, category & expiry for each reason you collect data.' },
  { num: '02', title: 'Build a notice', desc: 'Group purposes and publish a branded consent notice.' },
  { num: '03', title: 'Collect consent', desc: 'Data Principals accept or reject each purpose in your product.' },
  { num: '04', title: 'Audit & report', desc: 'Consent + audit logs written atomically for compliance.' },
];

export const STATS = [
  { v: '100%', l: 'Immutable audit coverage' },
  { v: '<1', l: 'Day to a compliant program' },
  { v: 'DPDP', l: 'Built for India’s 2023 Act' },
  { v: '∞', l: 'Tenants on one platform' },
];

/* Compliance credentials surfaced prominently (Trust & Authority) */
export const CREDENTIALS = [
  'DPDP Act 2023',
  'Verifiable consent',
  'Data Principal rights',
  'Breach notification',
  'Immutable audit trail',
  'RBAC least-privilege',
  'OAuth2 / SSO',
  'Data minimization',
];

/* Three authority pillars */
export const PILLARS = [
  {
    icon: <HistoryEduOutlinedIcon />,
    kpi: '100%',
    title: 'Append-only audit',
    desc: 'Every consent action is written atomically and can never be altered or deleted.',
  },
  {
    icon: <LockOutlinedIcon />,
    kpi: 'RBAC',
    title: 'Least-privilege access',
    desc: 'Role-based controls on every endpoint, with Google OAuth2 / SSO built in.',
  },
  {
    icon: <VerifiedOutlinedIcon />,
    kpi: 'Board-ready',
    title: 'Records for the Board',
    desc: 'Versioned notices and exportable consent proof for the Data Protection Board of India under the DPDP Act.',
  },
];

/* Self-assessment checklist — DPDP Act 2023 readiness items */
export const CHECKLIST = [
  {
    category: 'Notice & consent (§5–6)',
    items: [
      'Every purpose has an itemised notice, available in English and the Eighth Schedule languages',
      'Consent is free, specific, informed, unconditional and unambiguous via a clear affirmative action',
      'You collect only the personal data necessary for the stated purpose (data minimisation)',
      'Data Principals can withdraw consent as easily as they gave it',
    ],
  },
  {
    category: 'Data Principal rights (§11–14)',
    items: [
      'Data Principals can request a summary of the personal data you process about them',
      'Correction, completion, updation and erasure requests have an owned workflow',
      'A grievance-redressal mechanism is published and responsive within statutory timelines',
      'Data Principals can nominate another individual to exercise their rights',
    ],
  },
  {
    category: 'Children & special duties (§9)',
    items: [
      'Verifiable parental consent is obtained before processing a child’s data',
      'No tracking, behavioural monitoring or targeted advertising directed at children',
    ],
  },
  {
    category: 'Security & breach (§8)',
    items: [
      'Reasonable security safeguards protect personal data against a breach',
      'A personal data breach is notified to the Board and affected Data Principals',
      'Personal data is erased once the purpose is served and retention is no longer required',
    ],
  },
  {
    category: 'Accountability & the Board (§8, §10)',
    items: [
      'Every consent action is written to an immutable audit trail',
      'Consent records can be produced for the Data Protection Board on demand',
      'Significant Data Fiduciary duties — DPO, audits, DPIAs — are met where applicable',
    ],
  },
];

/* DPDP Act 2023 penalty Schedule — statutory maximums (in ₹) */
export const DPDP_BREACHES = [
  {
    key: 'security',
    label: 'Failure to take reasonable security safeguards',
    section: 'Section 8(5)',
    cap: 2500000000, // ₹250 crore
    basis: 'Failure to implement reasonable security safeguards to prevent a personal data breach — the highest penalty tier under the Act.',
  },
  {
    key: 'notify',
    label: 'Failure to notify a personal data breach',
    section: 'Section 8(6)',
    cap: 2000000000, // ₹200 crore
    basis: 'Failure to give the Data Protection Board or affected Data Principals notice of a personal data breach.',
  },
  {
    key: 'children',
    label: "Breach of children's data obligations",
    section: 'Section 9',
    cap: 2000000000, // ₹200 crore
    basis: 'Non-fulfilment of additional obligations for processing children’s data — verifiable parental consent, no tracking or targeted advertising.',
  },
  {
    key: 'sdf',
    label: 'Significant Data Fiduciary obligations',
    section: 'Section 10',
    cap: 1500000000, // ₹150 crore
    basis: 'Non-fulfilment of the additional obligations of a Significant Data Fiduciary — DPO appointment, independent audits, and impact assessments.',
  },
  {
    key: 'other',
    label: 'Any other breach of the Act or rules',
    section: 'Residuary',
    cap: 500000000, // ₹50 crore
    basis: 'Breach of any other provision of the DPDP Act or the rules made under it — the residuary penalty tier.',
  },
];

/* FAQ — original DPDP Act 2023 explainers (not copied from any source) */
export const FAQS = [
  {
    q: 'What is the Digital Personal Data Protection Act, 2023?',
    a: 'The DPDP Act is India’s dedicated personal-data protection law. It governs how organisations collect, store, use and share the personal data of individuals in India, and is enforced by the Data Protection Board of India. It runs on a consent-first model: most processing of personal data needs the free and informed consent of the individual, preceded by a clear notice of what is collected and why.',
  },
  {
    q: 'Who is a Data Principal, and who is a Data Fiduciary?',
    a: 'The Data Principal is the individual the personal data is about. The Data Fiduciary is the entity that decides the purpose and the means of processing that data. If you decide why and how customer or employee data is used, you are a Data Fiduciary and the Act’s obligations rest with you. A Data Processor only processes data on a Fiduciary’s behalf, under contract.',
  },
  {
    q: 'When do I actually need consent to process personal data?',
    a: 'As a rule, you need the Data Principal’s consent before processing their personal data for a stated purpose. The Act also allows a few “legitimate uses” without fresh consent — for example, data a person voluntarily provides for a requested service, or processing required for an employment relationship or legal obligation. Outside those, consent backed by a notice is the default.',
  },
  {
    q: 'What makes consent valid under the Act?',
    a: 'Consent must be free, specific, informed, unconditional and unambiguous, given by a clear affirmative action, and limited to the data necessary for the stated purpose. It must follow an itemised notice and be as easy to withdraw as to give. Pre-ticked boxes, bundled “accept all” toggles, and silent or implied consent do not meet the bar.',
  },
  {
    q: 'How does this platform keep me compliant?',
    a: 'You define each purpose with its linked personal-data items and retention, group them into versioned notices, and present granular per-purpose accept/reject choices. Every response becomes a consent log written atomically to an immutable audit trail — so you hold a defensible record of exactly which notice a Data Principal saw and what they agreed to, ready for the Board.',
  },
  {
    q: 'Can a Data Principal withdraw consent — and what happens then?',
    a: 'Yes, and withdrawal must be as easy as the original consent. The platform records each withdrawal, stops the affected purpose, and timestamps the change in the audit trail. Where consent was the only basis for processing, you should cease that processing and erase the data once retention is no longer required.',
  },
  {
    q: 'How is children’s data treated?',
    a: 'For anyone under 18, the Act requires verifiable parental consent and prohibits tracking, behavioural monitoring and advertising targeted at children. You can flag purposes that involve minors and route them through a parental-consent flow, with the verification captured in the same audit record.',
  },
  {
    q: 'What are my duties if a personal data breach occurs?',
    a: 'You must maintain reasonable security safeguards to prevent breaches, and on a breach you must notify both the Data Protection Board and every affected Data Principal. Your consent records and audit trail help you scope which individuals and purposes are affected so notifications go out quickly and accurately.',
  },
  {
    q: 'What are the penalties for non-compliance?',
    a: 'The Board can levy up to ₹250 crore for failing to take reasonable security safeguards, up to ₹200 crore for breach-notification or children-data failures, up to ₹150 crore for Significant Data Fiduciary lapses, and up to ₹50 crore for other breaches. The risk estimator shows the statutory maximum for each breach type.',
  },
  {
    q: 'Does the DPDP Act require data localisation?',
    a: 'By default the Act permits transferring personal data outside India, except to countries the Central Government specifically restricts by notification. Sector regulators (for example in banking or insurance) may still impose stricter localisation. The platform’s multi-tenant isolation keeps each organisation’s data and records cleanly separated wherever you host.',
  },
];
